import { useCallback } from 'react'
import useQueuedSave from 'src/hooks/useQueuedSave'
import { useDispatch, useSelector } from 'react-redux'
import { PatchContent, PatchData } from 'src/model/patch'
import {
  patchCollectionSettingsAction,
  patchCourseCollectionAction,
} from 'src/reducers/collection/action'

/**
 * Reads the open path (collection) and its settings from redux and exposes
 * patch helpers for both.
 */
const usePathSettings = () => {
  const dispatch = useDispatch()
  const collection = useSelector(state => state.collection.collection)
  const settings = useSelector(state => state.collection.collectionSettings)

  /** Patches one field of the collection settings entity. */
  const patchSetting = useCallback(
    (field, value, callback = () => {}, onError) => {
      const patch = new PatchData(null, collection.id)
      patch.addContent(
        new PatchContent(value, field, PatchData.OPERATION_REPLACE),
      )
      dispatch(patchCollectionSettingsAction(patch, callback, onError))
    },
    [dispatch, collection],
  )

  /** Patches one field of the collection entity itself (e.g. its name). */
  const patchCollection = useCallback(
    (field, value, callback = () => {}) => {
      const patch = new PatchData(null, collection.id)
      patch.addContent(
        new PatchContent(value, field, PatchData.OPERATION_REPLACE),
      )
      dispatch(patchCourseCollectionAction(patch, callback))
    },
    [dispatch, collection],
  )

  return { collection, settings: settings || {}, patchSetting, patchCollection }
}

/** Persists one settings field; resolves on success, rejects on failure. */
export const usePersistSetting = field => {
  const { patchSetting } = usePathSettings()
  return useCallback(
    value =>
      new Promise((resolve, reject) =>
        patchSetting(field, value, resolve, reject),
      ),
    [field, patchSetting],
  )
}

/**
 * One settings field as an instant-save value (see useQueuedSave). The
 * settings reducer mutates its object in place, so components can't rely on a
 * re-render from redux; the local value covers that.
 *
 * @returns {[any, (value: any) => void]}
 */
export const useSettingField = (field, fallback = false) => {
  const { settings } = usePathSettings()
  return useQueuedSave(settings[field] ?? fallback, usePersistSetting(field))
}

export default usePathSettings
